import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { Icon } from '@iconify/react';

/**
 * Dropdown Select Component
 * A customizable dropdown select for choosing values
 *
 * @param {Object} props
 * @param {Array} props.options - Array of { value, label, icon?, disabled?, description? }
 * @param {string|number} props.value - Current selected value
 * @param {function} props.onChange - Callback when value changes: (value, option) => void
 * @param {string} props.placeholder - Placeholder text when no value selected
 * @param {boolean} props.disabled - Disable the dropdown
 * @param {boolean} props.loading - Show loading state
 * @param {boolean} props.searchable - Enable search/filter functionality
 * @param {boolean} props.clearable - Show clear button when value selected
 * @param {'sm'|'md'|'lg'} props.size - Size variant
 * @param {string} props.className - Additional classes for container
 * @param {string} props.label - Label text above dropdown
 * @param {string} props.error - Error message to display
 * @param {string} props.hint - Hint text below dropdown
 * @param {boolean} props.fullWidth - Take full width of container
 */
export const Dropdown = forwardRef(function Dropdown({
  options = [],
  value,
  onChange,
  placeholder = '請選擇...',
  disabled = false,
  loading = false,
  searchable = false,
  clearable = false,
  size = 'md',
  className = '',
  label,
  error,
  hint,
  fullWidth = false,
  icon,
  ...props
}, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Find selected option
  const selectedOption = options.find(opt => opt.value === value);

  // Filter options based on search
  const filteredOptions = searchable && searchQuery
    ? options.filter(opt =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        opt.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus input when opening searchable dropdown
  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        if (!isOpen) {
          e.preventDefault();
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchQuery('');
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const handleSelect = (option) => {
    if (option.disabled) return;
    onChange?.(option.value, option);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(undefined, null);
  };

  const sizeClasses = {
    sm: 'py-1.5 px-3 text-xs min-h-[32px]',
    md: 'py-2 px-3 text-sm min-h-[40px]',
    lg: 'py-3 px-4 text-base min-h-[48px]',
  };

  const iconSizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${fullWidth ? 'w-full' : 'inline-block'} ${className}`}
      {...props}
    >
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-[var(--text-primary)] mb-1.5">
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        ref={ref}
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled || loading}
        className={`
          w-full flex items-center justify-between gap-2
          bg-[var(--bg-primary)] border rounded-lg
          text-left cursor-pointer transition-all duration-150
          outline-none
          ${sizeClasses[size]}
          ${error
            ? 'border-red-500 focus:ring-2 focus:ring-red-500/20'
            : isOpen
              ? 'border-[var(--button-primary)] ring-2 ring-[var(--button-primary)]/20'
              : 'border-[var(--border-color)] hover:border-[var(--text-tertiary)]'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed bg-[var(--bg-secondary)]' : ''}
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Left icon */}
          {icon && (
            <Icon icon={icon} className={`${iconSizes[size]} text-[var(--text-tertiary)] shrink-0`} />
          )}

          {/* Selected value or placeholder */}
          {selectedOption ? (
            <div className="flex items-center gap-2 min-w-0">
              {selectedOption.icon && (
                <Icon icon={selectedOption.icon} className={`${iconSizes[size]} text-[var(--text-secondary)] shrink-0`} />
              )}
              <span className="text-[var(--text-primary)] truncate">
                {selectedOption.label}
              </span>
            </div>
          ) : (
            <span className="text-[var(--text-tertiary)] truncate">
              {placeholder}
            </span>
          )}
        </div>

        {/* Right icons */}
        <div className="flex items-center gap-1 shrink-0">
          {/* Loading spinner */}
          {loading && (
            <Icon icon="mdi:loading" className={`${iconSizes[size]} text-[var(--text-tertiary)] animate-spin`} />
          )}

          {/* Clear button */}
          {clearable && selectedOption && !disabled && !loading && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors"
            >
              <Icon icon="mdi:close" className={iconSizes[size]} />
            </button>
          )}

          {/* Chevron */}
          {!loading && (
            <Icon
              icon="mdi:chevron-down"
              className={`${iconSizes[size]} text-[var(--text-tertiary)] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          )}
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl shadow-lg overflow-hidden">
          {/* Search input */}
          {searchable && (
            <div className="p-2 border-b border-[var(--border-color)]">
              <div className="relative">
                <Icon
                  icon="mdi:magnify"
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]"
                />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜尋..."
                  className="w-full pl-8 pr-3 py-2 text-sm bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg outline-none focus:border-[var(--button-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                />
              </div>
            </div>
          )}

          {/* Options list */}
          <div className="max-h-[240px] overflow-y-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <button
                  key={option.value ?? index}
                  type="button"
                  onClick={() => handleSelect(option)}
                  disabled={option.disabled}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors
                    ${option.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer hover:bg-[var(--bg-hover)]'
                    }
                    ${option.value === value ? 'bg-[var(--bg-active)]' : ''}
                  `}
                >
                  {/* Option icon */}
                  {option.icon && (
                    <Icon
                      icon={option.icon}
                      className={`${iconSizes[size]} text-[var(--text-secondary)] shrink-0`}
                    />
                  )}

                  {/* Option content */}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm text-[var(--text-primary)] ${option.value === value ? 'font-medium' : ''}`}>
                      {option.label}
                    </div>
                    {option.description && (
                      <div className="text-xs text-[var(--text-tertiary)] mt-0.5 truncate">
                        {option.description}
                      </div>
                    )}
                  </div>

                  {/* Check icon for selected */}
                  {option.value === value && (
                    <Icon
                      icon="mdi:check"
                      className="text-[var(--button-primary)] shrink-0"
                    />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-[var(--text-tertiary)]">
                沒有找到選項
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
          <Icon icon="mdi:alert-circle" className="text-sm" />
          {error}
        </p>
      )}

      {/* Hint text */}
      {hint && !error && (
        <p className="mt-1.5 text-xs text-[var(--text-tertiary)]">
          {hint}
        </p>
      )}
    </div>
  );
});

/**
 * Simple Select - Minimal dropdown without search
 */
export function SimpleSelect({
  options = [],
  value,
  onChange,
  placeholder = '選擇',
  size = 'sm',
  className = '',
  ...props
}) {
  return (
    <Dropdown
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      size={size}
      className={className}
      {...props}
    />
  );
}

/**
 * Grouped Dropdown - Dropdown with option groups
 * @param {Array} props.groups - Array of { label, options: [] }
 */
export function GroupedDropdown({
  groups = [],
  value,
  onChange,
  placeholder = '請選擇...',
  size = 'md',
  className = '',
  label,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Find selected option across all groups
  const selectedOption = groups.reduce((found, group) => {
    if (found) return found;
    return group.options.find(opt => opt.value === value);
  }, null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (option) => {
    if (option.disabled) return;
    onChange?.(option.value, option);
    setIsOpen(false);
  };

  const sizeClasses = {
    sm: 'py-1.5 px-3 text-xs min-h-[32px]',
    md: 'py-2 px-3 text-sm min-h-[40px]',
    lg: 'py-3 px-4 text-base min-h-[48px]',
  };

  return (
    <div ref={containerRef} className={`relative inline-block ${className}`} {...props}>
      {label && (
        <label className="block text-sm font-medium text-[var(--text-primary)] mb-1.5">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between gap-2
          bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg
          text-left cursor-pointer transition-all duration-150
          hover:border-[var(--text-tertiary)]
          ${sizeClasses[size]}
          ${isOpen ? 'border-[var(--button-primary)] ring-2 ring-[var(--button-primary)]/20' : ''}
        `}
      >
        <span className={selectedOption ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}>
          {selectedOption?.label || placeholder}
        </span>
        <Icon
          icon="mdi:chevron-down"
          className={`text-[var(--text-tertiary)] transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl shadow-lg overflow-hidden">
          <div className="max-h-[280px] overflow-y-auto">
            {groups.map((group, groupIndex) => (
              <div key={groupIndex}>
                {/* Group label */}
                <div className="px-3 py-2 text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider bg-[var(--bg-secondary)]">
                  {group.label}
                </div>

                {/* Group options */}
                {group.options.map((option, optIndex) => (
                  <button
                    key={option.value ?? optIndex}
                    type="button"
                    onClick={() => handleSelect(option)}
                    disabled={option.disabled}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors
                      ${option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[var(--bg-hover)]'}
                      ${option.value === value ? 'bg-[var(--bg-active)]' : ''}
                    `}
                  >
                    {option.icon && (
                      <Icon icon={option.icon} className="text-[var(--text-secondary)]" />
                    )}
                    <span className={`text-sm ${option.value === value ? 'font-medium text-[var(--text-primary)]' : 'text-[var(--text-primary)]'}`}>
                      {option.label}
                    </span>
                    {option.value === value && (
                      <Icon icon="mdi:check" className="ml-auto text-[var(--button-primary)]" />
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Account Dropdown - Specialized dropdown for selecting social accounts
 * @param {Array} props.accounts - Array of account objects { id, account_name, platform, status, ... }
 * @param {string} props.value - Selected account ID
 * @param {function} props.onChange - Callback when account changes: (accountId, account) => void
 */
export function AccountDropdown({
  accounts: externalAccounts,
  value,
  onChange,
  placeholder = '選擇帳號',
  size = 'md',
  className = '',
  label,
  showPlatformIcon = true,
  showStatus = false,
  fullWidth = true,
  autoFetch = true,
  platformFilter,
  onAccountsLoaded,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalAccounts, setInternalAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  // Use external accounts if provided, otherwise use internal
  const accounts = externalAccounts || internalAccounts;

  // Auto fetch accounts if no external accounts provided
  useEffect(() => {
    if (!externalAccounts && autoFetch) {
      const fetchAccounts = async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          const response = await fetch('/api/user/social-accounts/account-settings', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();

          let fetchedAccounts = data.data || data.accounts || [];

          // Filter out invalid accounts (must be active with token)
          fetchedAccounts = fetchedAccounts.filter(
            acc => acc.status === 'active' && acc.access_token
          );

          // Apply platform filter if specified
          if (platformFilter) {
            fetchedAccounts = fetchedAccounts.filter(
              acc => acc.platform === platformFilter
            );
          }

          setInternalAccounts(fetchedAccounts);
          onAccountsLoaded?.(fetchedAccounts);

          // Auto select first account if no value and accounts exist
          if (!value && fetchedAccounts.length > 0) {
            onChange?.(fetchedAccounts[0].id, fetchedAccounts[0]);
          }
        } catch (error) {
          console.error('Failed to fetch accounts:', error);
          setInternalAccounts([]);
        } finally {
          setLoading(false);
        }
      };
      fetchAccounts();
    }
  }, [externalAccounts, autoFetch, platformFilter]);

  // Find selected account
  const selectedAccount = accounts.find(acc => acc.id === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (account) => {
    onChange?.(account.id, account);
    setIsOpen(false);
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      threads: 'simple-icons:threads',
      instagram: 'mdi:instagram',
      facebook: 'mdi:facebook',
      twitter: 'mdi:twitter',
      tiktok: 'simple-icons:tiktok',
    };
    return icons[platform?.toLowerCase()] || 'mdi:account';
  };

  const getPlatformColor = (platform) => {
    const colors = {
      threads: 'text-black',
      instagram: 'text-pink-500',
      facebook: 'text-blue-600',
      twitter: 'text-sky-500',
      tiktok: 'text-black',
    };
    return colors[platform?.toLowerCase()] || 'text-gray-500';
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="w-2 h-2 rounded-full bg-green-500" />;
    }
    return <span className="w-2 h-2 rounded-full bg-gray-400" />;
  };

  const sizeClasses = {
    sm: 'py-1.5 px-3 text-xs min-h-[32px]',
    md: 'py-2 px-3 text-sm min-h-[40px]',
    lg: 'py-3 px-4 text-base min-h-[48px]',
  };

  const iconSizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${fullWidth ? 'w-full' : 'inline-block'} ${className}`}
      {...props}
    >
      {label && (
        <label className="block text-sm font-medium text-[var(--text-primary)] mb-1.5">
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        type="button"
        onClick={() => !loading && setIsOpen(!isOpen)}
        disabled={loading}
        className={`
          w-full flex items-center justify-between gap-2
          bg-[var(--bg-primary)] border rounded-lg
          text-left transition-all duration-150
          outline-none
          ${sizeClasses[size]}
          ${loading ? 'cursor-wait opacity-70' : 'cursor-pointer'}
          ${isOpen
            ? 'border-[var(--button-primary)] ring-2 ring-[var(--button-primary)]/20'
            : 'border-[var(--border-color)] hover:border-[var(--text-tertiary)]'
          }
        `}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <Icon icon="mdi:loading" className={`${iconSizes[size]} animate-spin text-[var(--text-tertiary)]`} />
            <span className="text-[var(--text-tertiary)]">載入中...</span>
          </div>
        ) : selectedAccount ? (
          <div className="flex items-center gap-2.5 min-w-0">
            {showPlatformIcon && (
              <Icon
                icon={getPlatformIcon(selectedAccount.platform)}
                className={`${iconSizes[size]} ${getPlatformColor(selectedAccount.platform)} shrink-0`}
              />
            )}
            <span className="text-[var(--text-primary)] truncate">
              @{selectedAccount.account_name}
            </span>
            {showStatus && getStatusBadge(selectedAccount.status)}
          </div>
        ) : (
          <span className="text-[var(--text-tertiary)]">{placeholder}</span>
        )}

        <Icon
          icon={loading ? "mdi:loading" : "mdi:chevron-down"}
          className={`${iconSizes[size]} text-[var(--text-tertiary)] transition-transform duration-200 shrink-0 ${loading ? 'animate-spin' : isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl shadow-lg overflow-hidden py-1">
          {accounts.length > 0 ? (
            accounts.map((account) => (
              <button
                key={account.id}
                type="button"
                onClick={() => handleSelect(account)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors
                  cursor-pointer hover:bg-[var(--bg-hover)]
                  ${account.id === value ? 'bg-[var(--bg-active)]' : ''}
                `}
              >
                {/* Platform icon */}
                {showPlatformIcon && (
                  <Icon
                    icon={getPlatformIcon(account.platform)}
                    className={`${iconSizes[size]} ${getPlatformColor(account.platform)} shrink-0`}
                  />
                )}

                {/* Account info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm truncate ${account.id === value ? 'font-medium' : ''} text-[var(--text-primary)]`}>
                      @{account.account_name}
                    </span>
                    {showStatus && getStatusBadge(account.status)}
                  </div>
                  <div className="text-xs text-[var(--text-tertiary)] mt-0.5 capitalize">
                    {account.platform}
                  </div>
                </div>

                {/* Check icon */}
                {account.id === value && (
                  <Icon icon="mdi:check" className="text-[var(--button-primary)] shrink-0" />
                )}
              </button>
            ))
          ) : (
            <div className="px-3 py-6 text-center text-sm text-[var(--text-tertiary)]">
              <Icon icon="mdi:account-off" className="text-2xl mb-2 opacity-50" />
              <p>尚未連結帳號</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Dropdown;
